import { SolTableColumnDef } from './sol-table-column-def';
import { SolTableActionGroup } from './sol-table-action-group';
import { ObjectTools } from '../../tools/object-tools';
import { SolObjectType } from '../core/sol-type';
import { SolTableSort } from './sol-table-sort';

export type SolTableClickBehavior = 'none' | 'click' | 'expand';

export class SolTableDef<S> {
  private constructor(
    public readonly idFn: (item: S) => string,
    public readonly selectable: boolean,
    public readonly clickBehavior: SolTableClickBehavior,
    public readonly object: SolObjectType<S>,
    public readonly columns: SolTableColumnDef<any, any>[],
    public readonly menu: SolTableActionGroup<S>[] | undefined,
    public readonly id: string | undefined,
    public readonly persistColumns: boolean,
    public readonly defaultSort: SolTableSort | undefined,

    // Derived
    public readonly persistedColumnsTableId: string | undefined,
  ) {}

  public static of<S>(idFn: (item: S) => string, object: SolObjectType<S>, columns: SolTableColumnDef<any, any>[]) {
    return new SolTableDef(idFn, false, 'none', object, columns, undefined, undefined, true, undefined, undefined);
  }

  public withId(id: string): SolTableDef<S> {
    return this.copy({ id }).recalculatePersistedColumnsTableId();
  }

  /**
   * The sort the table opens on when nothing else has spoken — a URL, or the caller's stored layout.
   * Declaring it here rather than on the table is what lets a stored layout tell an untouched default
   * apart from a sort somebody actually chose.
   */
  public withDefaultSort(defaultSort: SolTableSort): SolTableDef<S> {
    return this.copy({ defaultSort });
  }

  public withoutPersistedColumns(): SolTableDef<S> {
    return this.copy({ persistColumns: false }).recalculatePersistedColumnsTableId();
  }

  public withIdFn(idFn: (item: S) => string): SolTableDef<S> {
    return this.copy({ idFn });
  }

  public withSelectable(selectable: boolean): SolTableDef<S> {
    return this.copy({ selectable });
  }

  public withClickable(clickable: boolean): SolTableDef<S> {
    return this.copy({ clickBehavior: clickable ? 'click' : 'none' });
  }

  public withClickBehavior(clickBehavior: SolTableClickBehavior): SolTableDef<S> {
    return this.copy({ clickBehavior });
  }

  public withObject(object: SolObjectType<S>): SolTableDef<S> {
    return this.copy({ object });
  }

  public withColumns(columns: SolTableColumnDef<any, any>[]): SolTableDef<S> {
    return this.copy({ columns });
  }

  public withMenu(menu: SolTableActionGroup<S>[]): SolTableDef<S> {
    return this.copy({ menu });
  }

  public sorts(columnId: string): boolean {
    return this.columns.some(column => column.id === columnId && column.sortable);
  }

  private recalculatePersistedColumnsTableId(): SolTableDef<S> {
    const persistedColumnsTableId = this.persistColumns ? this.id : undefined;
    if (this.persistedColumnsTableId === persistedColumnsTableId) {
      return this;
    }
    return this.copy({ persistedColumnsTableId });
  }

  private copy(props: Partial<SolTableDef<S>>): SolTableDef<S> {
    return ObjectTools.copy<SolTableDef<S>>(this, props);
  }
}
